import React, { ReactNode } from "react";
import ProfileContainer from "./ProfileContainer";
import useInvestorMarkerStore from "../zstore/investorMarkers";
import { Grid, Stack, Typography } from "@mui/material";
import { InvestorMarker } from "../api/transformers/marker";
import MarkerDisplay from "./MarkerDisplay";
import { find } from "lodash";
import { MarkerGroups, NoneGroupMarkers } from "../constant/markerConstant";
import MarkerUpdateButton from "./MarkerUpdateButton";
import useInvestorAmlDetailStore from "../zstore/investorAmlDetail";
import { useShallow } from "zustand/react/shallow";
import MarkerDialog, { useMarkerDialog } from "./MarkerDialog";
import { selectors } from "../amlSlice";
import useHighlightStore, { ItemType } from "../zstore/highlightStore";
import { useSelector } from "react-redux";

type PepAndSanctionsProfileProps = {
  personId: string;
};

const PepAndSanctionsProfile: React.FC<PepAndSanctionsProfileProps> = ({
  personId,
}) => {
  const {
    data: investorMarkers,
    loading: investorMarkerLoading,
    fetchData: refetchInvestorMarkers,
  } = useInvestorMarkerStore();
  const { amlDetail, amlDetailLoading } = useInvestorAmlDetailStore(
    useShallow((state) => ({
      amlDetail: state.data,
      amlDetailLoading: state.loading,
    }))
  );
  const removeHighlightedItem = useHighlightStore((state) => state.removeItem);
  const markers = useSelector(selectors.markers);
  const {
    open: markerDialogOpen,
    closeDialog: closeMarkerDialog,
    formConfig: markerDialogFormConfig,
    initialValue: markerDialogInitial,
    openMarkerAddDialog,
    openMarkerUpdateDialog,
    openGroupMarkerAddDialog,
  } = useMarkerDialog();
  const sanctionsMarker = find(investorMarkers, {
    groupCode: MarkerGroups.SANS,
    removed: false,
  });
  const pepMarker = find(investorMarkers, {
    groupCode: MarkerGroups.PEPS,
    removed: false,
  });
  const advMarker = find(investorMarkers, {
    code: NoneGroupMarkers.ADV,
    removed: false,
  });
  const rcaMarker = find(investorMarkers, {
    code: NoneGroupMarkers.RCA,
    removed: false,
  });
  return (
    <ProfileContainer id="marker-section" title="PEP and Sanctions">
      <Stack direction="column" rowGap="10px">
        <MarkerRow
          marker={sanctionsMarker}
          loading={investorMarkerLoading}
          label="Sanctions Status: "
        >
          <MarkerUpdateButton
            markerCode={sanctionsMarker?.code}
            disabled={amlDetailLoading || investorMarkerLoading}
            onClick={() => {
              if (sanctionsMarker) {
                openMarkerUpdateDialog(sanctionsMarker, amlDetail!);
              } else {
                openGroupMarkerAddDialog(MarkerGroups.SANS, amlDetail!);
              }
            }}
          />
        </MarkerRow>
        <MarkerRow
          marker={pepMarker}
          loading={investorMarkerLoading}
          label="PEP Status: "
        >
          <MarkerUpdateButton
            markerCode={pepMarker?.code}
            disabled={amlDetailLoading || investorMarkerLoading}
            onClick={() => {
              if (pepMarker) {
                openMarkerUpdateDialog(pepMarker, amlDetail!);
              } else {
                openGroupMarkerAddDialog(MarkerGroups.PEPS, amlDetail!);
              }
            }}
          />
        </MarkerRow>
        <MarkerRow
          marker={advMarker}
          loading={investorMarkerLoading}
          label="Adverse Media: "
        >
          <MarkerUpdateButton
            markerCode={advMarker?.code}
            disabled={amlDetailLoading || investorMarkerLoading}
            onClick={() => {
              if (advMarker) {
                openMarkerUpdateDialog(advMarker, amlDetail!);
              } else {
                openMarkerAddDialog(NoneGroupMarkers.ADV, amlDetail!);
              }
            }}
          />
        </MarkerRow>
        <MarkerRow
          marker={rcaMarker}
          loading={investorMarkerLoading}
          label="Relative or Close Associate: "
        >
          <MarkerUpdateButton
            markerCode={rcaMarker?.code}
            disabled={amlDetailLoading || investorMarkerLoading}
            onClick={() => {
              if (rcaMarker) {
                openMarkerUpdateDialog(rcaMarker, amlDetail!);
              } else {
                openMarkerAddDialog(NoneGroupMarkers.RCA, amlDetail!);
              }
            }}
          />
        </MarkerRow>
        <MarkerDialog
          open={markerDialogOpen}
          formConfig={markerDialogFormConfig!}
          initialValue={markerDialogInitial!}
          markers={markers}
          onClose={closeMarkerDialog}
          onSuccess={(data) => {
            closeMarkerDialog();
            refetchInvestorMarkers(personId, markers);
            removeHighlightedItem({
              id: data.previous!,
              type: ItemType.MARKER,
            });
          }}
        />
      </Stack>
    </ProfileContainer>
  );
};

const MarkerRow: React.FC<{
  marker?: InvestorMarker;
  loading: boolean;
  label: string;
  children?: ReactNode;
}> = ({ marker, loading, label, children }) => {
  return (
    <Grid alignItems="center" container>
      <Grid item xs={3}>
        <Typography sx={{ fontStyle: "italic" }}>{label}</Typography>
      </Grid>
      <Grid item xs={1.5}>
        <MarkerDisplay marker={marker} loading={loading} />
      </Grid>
      <Grid item xs={2}>
        {children}
      </Grid>
    </Grid>
  );
};

export default PepAndSanctionsProfile;
